import type { AssignmentResponseWithDetails } from '@repo/types/assignments';
import type { QuizAttemptPublic, QuizPublic } from '@repo/types/quiz';

import { getBestQuizResponse } from '@repo/lib/quiz/getBestQuizResponse';
import { getQuizAttemptTimeUnit } from '@repo/lib/quiz/getQuizAttemptTimeUnit';
import { format, formatDistanceStrict } from 'date-fns';
import { EyeIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getAssignmentAttemptBadgeMeta } from '@/utils/attempt-status';

interface AssignmentAttemptsTableProps {
  assignmentId: number;
  /** Submissions in chronological order (oldest first). */
  submissions: AssignmentResponseWithDetails[];
  /** Quiz backing the assignment, when there is one. */
  quiz?: QuizPublic | null;
  /**
   * The user's attempts on that quiz. Joined to submissions by quizRespId so
   * each row can show its own result instead of just "a quiz happened".
   */
  quizAttempts?: QuizAttemptPublic[];
  /** Whether the assignment grades itself off the quiz score. */
  autoGrade?: boolean;
}

export function AssignmentAttemptsTable(props: AssignmentAttemptsTableProps) {
  const { assignmentId, submissions, quiz, quizAttempts = [], autoGrade = false } = props;
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const isQuizBacked: boolean = quiz != null;

  const attemptsById = useMemo<Map<string, QuizAttemptPublic>>(() => new Map(quizAttempts.map((attempt) => [attempt.id, attempt])), [quizAttempts]);

  // Several attempts are allowed, so which one actually counts needs saying.
  const bestAttemptId = useMemo<string | undefined>(() => getBestQuizResponse(quizAttempts)?.id, [quizAttempts]);

  const columnCount: number = isQuizBacked ? 7 : 5;

  return (
    <div className="bg-background relative w-full overflow-x-auto rounded-2xl border">
      <Table>
        <TableHeader className="border-border-primary border-b">
          <TableRow>
            <TableHead>Podejście</TableHead>
            <TableHead>{isQuizBacked ? 'Czas rozpoczęcia' : 'Data'}</TableHead>
            <TableHead>Status</TableHead>
            {isQuizBacked ? (
              <>
                <TableHead>Punkty</TableHead>
                <TableHead className="hidden sm:table-cell">Wynik</TableHead>
                <TableHead className="hidden md:table-cell">Czas podejścia</TableHead>
              </>
            ) : (
              <TableHead className="hidden sm:table-cell">Pliki</TableHead>
            )}
            <TableHead> </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {submissions.length === 0 && (
            <TableRow>
              <TableCell colSpan={columnCount} className="text-center">
                Brak podejść
              </TableCell>
            </TableRow>
          )}
          {submissions.map((submission, index) => {
            const attempt = submission.quizRespId ? attemptsById.get(submission.quizRespId) : undefined;
            const badge = getAssignmentAttemptBadgeMeta(submission, {
              attempt,
              autoGrade,
            });
            const score: number | null = attempt?.score ?? null;
            const percentage: number | null = score != null && quiz != null && quiz.points > 0 ? (score / quiz.points) * 100 : null;
            const isBest: boolean = bestAttemptId != null && attempt?.id === bestAttemptId;

            return (
              <TableRow key={submission.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {index + 1}
                    {isBest && (
                      <Badge variant="success" border>
                        Najlepsze
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>{format(attempt?.createdAt ?? submission.createdAt, 'dd.MM.yyyy HH:mm')}</TableCell>
                <TableCell>
                  <Badge variant={badge.variant} border>
                    {badge.label}
                  </Badge>
                </TableCell>
                {isQuizBacked ? (
                  <>
                    <TableCell>
                      {score == null ? '-' : score} / {quiz?.points ?? 0}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">{percentage == null ? '-' : `${percentage.toPrecision(3)}%`}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {attempt?.completedAt ? formatDistanceStrict(attempt.completedAt, attempt.createdAt, { unit: getQuizAttemptTimeUnit(attempt) }) : '-'}
                    </TableCell>
                  </>
                ) : (
                  <TableCell className="hidden sm:table-cell">{submission.files.length}</TableCell>
                )}
                <TableCell>
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(
                          submission.quizRespId && quiz
                            ? `/quizzes/${quiz.id}/attempt/${submission.quizRespId}/result?backTo=${encodeURIComponent(pathname + search)}`
                            : `/assignments/${assignmentId}/submission/${submission.id}`
                        )
                      }
                    >
                      <EyeIcon />
                      Podgląd
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
